import type { Group, User } from '@gcv/shared';
import { TaskStatus, TaskUser } from '@gcv/shared';
import { DateTime } from 'luxon';

import type { DecoratedTask, TaskDetails } from '../../tasks/tasks.model';
import { getAssigneeName } from '../../tasks/util';

const sortDatesAscending = (dates: (string | undefined)[]) =>
  dates.filter((d): d is string => !!d).sort((a, b) => (DateTime.fromISO(a) > DateTime.fromISO(b) ? 1 : -1));

export const getTaskDetails = (decoratedTask: DecoratedTask, bankGroups: Group[]): TaskDetails => {
  const task = decoratedTask.tasks[0];
  const assignee = getAssigneeName(
    bankGroups,
    task.assigned_users,
    task.assigned_group_ids,
    task.assigned_groups
  );
  const completedUser =
    task.completed_by_user && task.completed_by_user === TaskUser.AUTO_GENERATED_USER
      ? 'Autocompleted'
      : (task.completed_by_user as User) &&
        (task.completed_by_user as User)?.firstName + ' ' + (task.completed_by_user as User).lastName;
  const account = task.account?.orgType === 'bank' ? '--' : task.account?.name;
  const createdByUser =
    task.created_by_user && task.created_by_user === TaskUser.AUTO_GENERATED_USER
      ? 'Auto Generated'
      : task.created_by_user && task.created_by_user.firstName && task.created_by_user.lastName
      ? `${task.created_by_user.firstName} ${task.created_by_user.lastName}`
      : '';
  const openTasks = decoratedTask.tasks.filter(
    (t) => t.status === TaskStatus.OPEN || t.status === TaskStatus.PROCESSING
  );
  const firstOpenTask = openTasks[0];
  const lastReminderDates = sortDatesAscending(openTasks.map((t) => t.last_reminder_date));
  const nextReminderDates = sortDatesAscending(openTasks.map((t) => t.next_reminder_date));

  return {
    account: account ?? '--',
    assignee: assignee === '' ? task.assigned_org.name : assignee,
    associatedWith: task.associated_with ?? '--',
    comment_id_components: task.comment_id_components,
    comment_type: task.comment_type,
    completedBy: completedUser ?? '--',
    completedDate: task.completed_on,
    completedNote: task.completed_note ?? '--',
    createdDate: task.date_created,
    createdBy: createdByUser,
    data: task.data,
    description: task.description ?? '--',
    dueDate: firstOpenTask?.process_date ?? task.process_date,
    groupedTasks: decoratedTask.tasks,
    id: task.id === '--' ? decoratedTask.groupKey : task.id,
    internalType: task.internal_type,
    isGrouped: decoratedTask.isGrouped,
    name: task.title ?? '--',
    status: task.status,
    taskCategory: task.task_category,
    lastReminderDate: lastReminderDates[lastReminderDates.length - 1] ?? '',
    nextReminderDate: nextReminderDates[0] ?? ''
  };
};
